/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Stack } from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { TimeSeriesService } from '@opengeoweb/shared';
import { CustomTooltip } from '@opengeoweb/shared';
import {
  mapSelectors,
  loadingIndicatorActions,
  genericSelectors,
  genericActions,
  syncGroupsActions,
  mapActions,
  useRegisterFeatureSyncSource,
  useSyncgroupTimeState,
} from '@opengeoweb/store';
import { LockLocked, LockUnlocked, Menu } from '@opengeoweb/theme';
import { webmapUtils } from '@opengeoweb/webmap';
import { LocationInfoButtonConnect } from './LocationInfo/LocationInfoButtonConnect';
import { TimeSeriesMarkersSwitch } from './TimeSeriesMarkersSwitch';
import { TimeSeriesTopRowButton } from './TimeSeriesTopRowButton';
import { TimeSeriesView } from './TimeSeriesView';
import type { Parameter, PlotPreset, TimeSeriesLocation } from './types';
import { consolidateFeatures, createGeoJsonArrays } from './utils';
import { timeSeriesActions } from '../../store';
import {
  getCurrentLocation,
  getGeoJSONWithService,
  getIsLegendVisible,
  getIsMapPinDisabled,
  getPlotState,
  getServices,
  getShouldHideLocationMarkers,
} from '../../store/selectors';
import type { TimeSeriesRootState } from '../../store/types';
import { useTimeseriesTranslation } from '../../utils/i18n';
import type { GetLocationsOption } from '../../utils/timeseries-api/hooks';
import { useGetLocations } from '../../utils/timeseries-api/hooks';
import TimeSeriesManagerMapButtonConnect from '../TimeSeriesManager/TimeSeriesManagerMapButtonConnect';
import { LocationSelectButtonConnect } from './LocationSelect/LocationSelectButtonConnect';

export const getUniqueServicesFromPreset = (
  parameters: Parameter[],
  services: TimeSeriesService[],
): GetLocationsOption[] => {
  if (!parameters.length || !services.length) {
    return [];
  }
  const options = parameters
    .map(({ serviceId, collectionId }) => {
      const service = services.find((item) => item.id === serviceId);
      return service ? { service, collectionId } : null;
    })
    // Filter out null values
    .filter((item): item is GetLocationsOption => item !== null);

  // Remove duplicates
  const uniqueOptions = options.filter(
    (current, index, array) =>
      index ===
      array.findIndex(
        (item) =>
          item.service.id === current.service.id &&
          item.collectionId === current.collectionId,
      ),
  );
  return uniqueOptions;
};

const useMapPin = ({
  panelId,
}: {
  panelId: string;
}): {
  toggleDisableMapPin: () => void;
  isMapPinDisabled: boolean;
} => {
  const dispatch = useDispatch();

  const selectedMapIds = useSelector((state: TimeSeriesRootState) =>
    genericSelectors.getLinkedTargetIds(state, panelId),
  );

  const isMapPinDisabled = useSelector((state: TimeSeriesRootState) =>
    getIsMapPinDisabled(state, panelId),
  );

  /* Make sure the mappin is visible when linking new maps */
  useEffect(() => {
    const toggleMapPinVisibility = (displayMapPin: boolean): void => {
      selectedMapIds.forEach((mapId) => {
        dispatch(
          mapActions.toggleMapPinIsVisible({
            mapId,
            displayMapPin,
          }),
        );
      });
    };

    toggleMapPinVisibility(true);

    return (): void => {
      // Hide the map pin when the component is unmounted
      toggleMapPinVisibility(false);
    };
  }, [dispatch, selectedMapIds]);

  const toggleDisableMapPin = (): void => {
    const newDisableMapPinState = !isMapPinDisabled;

    dispatch(
      timeSeriesActions.disableMapPin({
        panelId,
        isMapPinDisabled: newDisableMapPinState,
      }),
    );

    selectedMapIds.forEach((mapId) => {
      dispatch(
        mapActions.setDisableMapPin({
          mapId,
          disableMapPin: newDisableMapPinState,
        }),
      );
    });
  };

  return { toggleDisableMapPin, isMapPinDisabled };
};

export interface TimeSeriesConnectProps {
  panelId: string;
}

export const TimeSeriesConnect: React.FC<TimeSeriesConnectProps> = ({
  panelId,
}) => {
  const dispatch = useDispatch();
  const { t } = useTimeseriesTranslation();
  const geojsonLayerId = React.useRef(webmapUtils.generateLayerId()).current;

  const shouldShowLegend = useSelector((state: TimeSeriesRootState) =>
    getIsLegendVisible(state, panelId),
  );
  const services = useSelector((state: TimeSeriesRootState) =>
    getServices(state),
  );
  const shouldHideLocationMarkers = useSelector((state: TimeSeriesRootState) =>
    getShouldHideLocationMarkers(state, panelId),
  );
  const plotPresetState: PlotPreset | undefined = useSelector(
    (state: TimeSeriesRootState) => getPlotState(state, panelId),
  );
  const linkedMapIds = useSelector((state: TimeSeriesRootState) =>
    genericSelectors.getLinkedTargetIds(state, panelId),
  );
  const panelMapId = linkedMapIds?.[0] ?? plotPresetState?.mapId;
  const timeSeriesLocation = useSelector((state: TimeSeriesRootState) =>
    getCurrentLocation(state, panelId),
  );
  const selectedFeatureFromPanel = useSelector((state: TimeSeriesRootState) =>
    genericSelectors.getSelectedFeature(state, panelId),
  );
  const selectedFeatureFromMap = useSelector((state: TimeSeriesRootState) =>
    panelMapId ? genericSelectors.getSelectedFeature(state, panelMapId) : null,
  );
  const selectedFeature = selectedFeatureFromPanel ?? selectedFeatureFromMap;
  const allMaps = useSelector((store: TimeSeriesRootState) =>
    mapSelectors.getAllMapsByIds(store),
  );

  const { toggleDisableMapPin, isMapPinDisabled } = useMapPin({
    panelId,
  });

  const geoJSONWithService = useSelector((state: TimeSeriesRootState) =>
    getGeoJSONWithService(state, panelId),
  );

  const geojson = useSelector(
    (state: TimeSeriesRootState) =>
      genericSelectors.selectFeatureSyncLayersForPanel(state, panelId)[0]
        ?.geoJSON,
  );

  const isLoading = useCallback(
    (startOrFinish: boolean): void => {
      dispatch(
        loadingIndicatorActions.setEDRIsLoading({
          id: panelId,
          isEDRLoading: startOrFinish,
        }),
      );
    },
    [dispatch, panelId],
  );

  const setTimeSeriesLocation = useCallback(
    (location: TimeSeriesLocation | undefined): void => {
      dispatch(timeSeriesActions.setCurrentLocation({ panelId, location }));
    },
    [dispatch, panelId],
  );

  const memoizedSelectedFeature: TimeSeriesLocation | null =
    React.useMemo(() => {
      if (!selectedFeature) {
        return null;
      }

      return {
        lat: selectedFeature.lat,
        lon: selectedFeature.lon,
        id: selectedFeature.id,
        occurrences: selectedFeature.occurrences,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFeature?.lat, selectedFeature?.lon]);

  useEffect(() => {
    if (!memoizedSelectedFeature && panelMapId) {
      const activeMapCoordinates = allMaps[panelMapId]?.mapPinLocation;

      if (
        activeMapCoordinates?.lat !== undefined &&
        activeMapCoordinates?.lon !== undefined
      ) {
        setTimeSeriesLocation(activeMapCoordinates);
      }
      return;
    }

    if (memoizedSelectedFeature) {
      const validLocation: TimeSeriesLocation = {
        lat: memoizedSelectedFeature.lat,
        lon: memoizedSelectedFeature.lon,
        id: memoizedSelectedFeature.id,
        occurrences: memoizedSelectedFeature.occurrences,
      };

      setTimeSeriesLocation(validLocation);
    }
  }, [
    panelMapId,
    selectedFeature,
    setTimeSeriesLocation,
    memoizedSelectedFeature,
    allMaps,
  ]);

  useEffect(
    () => (): void => {
      if (panelId) {
        dispatch(syncGroupsActions.deleteFeatureSyncState({ panelId }));
      }
    },
    [dispatch, panelId],
  );

  useRegisterFeatureSyncSource(panelId);

  const parameters = useMemo(
    () => plotPresetState?.parameters || [],
    [plotPresetState?.parameters],
  );
  const stableServices = useMemo(() => services || [], [services]);

  const serviceListForLocations = useMemo(
    () => getUniqueServicesFromPreset(parameters, stableServices),
    [parameters, stableServices],
  );
  const { data: locationsData, isFetching: isLocationsFetching } =
    useGetLocations(serviceListForLocations);

  /* Set the loading state based on the locations fetching status */
  useEffect(() => {
    isLoading(isLocationsFetching);
  }, [isLoading, isLocationsFetching]);

  /* Create GeoJSON arrays and draw the geojson layer based on the locationsData */
  useEffect(() => {
    if (stableServices.length) {
      const { combinedGeoJson } = createGeoJsonArrays(
        locationsData,
        stableServices,
        shouldHideLocationMarkers,
        memoizedSelectedFeature,
      );

      if (combinedGeoJson.features.length === 0) {
        setTimeSeriesLocation(undefined);
      }

      const consolidatedGeoJSON = consolidateFeatures([
        {
          id: 'combined-layer',
          geoJSON: combinedGeoJson,
        },
      ]);

      dispatch(
        genericActions.addFeatureSyncState({
          panelId,
          data: {
            featureLayersById: {
              [geojsonLayerId]: {
                layerId: geojsonLayerId,
                geoJSON: consolidatedGeoJSON,
              },
            },
          },
        }),
      );
    }
  }, [
    dispatch,
    geojsonLayerId,
    shouldHideLocationMarkers,
    locationsData,
    memoizedSelectedFeature,
    panelId,
    stableServices,
    setTimeSeriesLocation,
  ]);

  const toggleLocationMarkers = (shouldHide: boolean): void => {
    dispatch(
      timeSeriesActions.toggleLocationMarkers({
        panelId,
        shouldHideLocationMarkers: shouldHide,
      }),
    );
  };

  const toggleLegend = (): void => {
    dispatch(
      timeSeriesActions.toggleLegend({
        panelId,
        isLegendVisible: !shouldShowLegend,
      }),
    );
  };

  const onToggleLegendParam = (parameterId: string): void => {
    dispatch(
      timeSeriesActions.toggleParameter({
        parameterId,
        panelId,
      }),
    );
  };

  const syncgroupTimeState = useSyncgroupTimeState(panelId, 1000);
  return (
    <Stack
      data-testid="TimeSeriesConnect"
      sx={{
        padding: '20px',
        width: '100%',
        height: '100%',
        backgroundColor: 'geowebColors.background.surfaceApp',
        containerType: 'inline-size',
        containerName: 'time-series-container',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          maxWidth: '750px',
          marginBottom: '4px',
          marginLeft: '10px',
        }}
      >
        <TimeSeriesManagerMapButtonConnect viewId={panelId} />
        {!!geojson && <LocationSelectButtonConnect panelId={panelId} />}
        <LocationInfoButtonConnect panelId={panelId} t={t} />

        <CustomTooltip
          title={
            isMapPinDisabled
              ? t('timeseries-unlock-location')
              : t('timeseries-lock-location')
          }
        >
          <span>
            <TimeSeriesTopRowButton
              data-testid="toggleLockLocationButton"
              icon={isMapPinDisabled ? <LockLocked /> : <LockUnlocked />}
              longText={
                isMapPinDisabled
                  ? t('timeseries-unlock-location')
                  : t('timeseries-lock-location')
              }
              onClick={(): void => toggleDisableMapPin()}
              selected={isMapPinDisabled}
            />
          </span>
        </CustomTooltip>
        <TimeSeriesMarkersSwitch
          shouldHideMarkers={shouldHideLocationMarkers}
          toggleMarkers={toggleLocationMarkers}
        />
        <CustomTooltip
          title={
            shouldShowLegend
              ? t('timeseries-legend-tooltip-hide')
              : t('timeseries-legend-tooltip-show')
          }
        >
          <span>
            <TimeSeriesTopRowButton
              icon={<Menu />}
              longText={t('timeseries-legend')}
              onClick={toggleLegend}
              selected={shouldShowLegend}
            />
          </span>
        </CustomTooltip>
      </Stack>
      {!!timeSeriesLocation && !!plotPresetState && (
        <Box sx={{ overflow: 'auto' }}>
          {!!services && (
            <TimeSeriesView
              isLoading={isLoading}
              onToggleLegendParam={onToggleLegendParam}
              plotPreset={plotPresetState}
              selectedLocation={timeSeriesLocation}
              services={services}
              shouldShowLegend={shouldShowLegend}
              splitGeoJsonByCollection={geoJSONWithService || []}
              syncgroupTimeState={syncgroupTimeState}
            />
          )}
        </Box>
      )}
    </Stack>
  );
};
